import { Outlet, Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/auth'
import { usePermission } from '@/hooks/usePermission'
import { cn } from '@/lib/utils'
import { languages } from '@/lib/i18n'
import {
  LayoutDashboard,
  BarChart3,
  Shield,
  MessageSquare,
  Settings,
  LogOut,
  Menu,
  ChevronDown,
  Globe,
  Building2,
  Building,
  Layers,
  Users,
  ChevronRight,
  Database,
  ServerCog,
  Search,
  PenTool,
  FolderOpen,
  Target,
  Workflow,
  BookOpen,
  Library,
  Rocket,
  Wand2,
  FileText,
  Server,
  Play,
  Brain,
  UserX,
  Sparkles,
  User,
  KeyRound,
  Bell,
  Compass,
  PlugZap,
  GitBranch,
  Gauge,
  Flame,
  AlertTriangle,
  Bot,
  Briefcase,
  Truck,
  FileCode,
  Fingerprint,
  ShieldAlert,
  Activity,
  Repeat,
  Copy,
  PackageOpen,
  ListChecks,
  GitMerge,
  Zap,
  ScrollText,
  BadgeDollarSign,
  LucideIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useTheme } from '@/hooks/useTheme'
import { Sun, Moon } from 'lucide-react'

// Workspace color types (Nav Restructure Fase 1 + DS v1.0)
type WorkspaceColor = 'home' | 'extract' | 'reconstruct' | 'design' | 'govern' | 'intelligence' | 'ai' | 'admin'

// Navigation item interface
interface NavItem {
  name: string
  href: string
  icon: LucideIcon
  adminLevel?: number // Max admin level that can see this item (1=GAM, 2=CP, 3=Holding, 4=Empresa, 5=Area)
  /** Single permission required to see this item */
  permission?: string
  /** Multiple permissions - any one grants access (OR logic) */
  permissions?: string[]
  /** INC-077: Product required to see this item (docs, pm, fiori, data, security, ai) */
  product?: string
  /** Group header i18n key for semantic grouping in sidebar */
  group?: string
  /** True on the first item of a group to render divider + label */
  groupFirst?: boolean
  /** DS: red notification badge count shown to the right of the label */
  badge?: number
}

// Workspace section interface
interface WorkspaceSection {
  id: string
  name: string
  icon: LucideIcon
  color: WorkspaceColor
  items: NavItem[]
  adminOnly?: boolean
  adminLevel?: number // Min role level to see this domain (1=GAM, 2=CP, 3=Holding...)
  /** Feature flag required to see this domain */
  feature?: string
  /** INC-077: Product required to see this domain (docs, pm, fiori, data, security, ai) */
  product?: string
}

// Workspace color mapping — DS v1.0 (usa escalas Tailwind ya mapeadas a tokens DS)
// Mapping semántico → Tailwind DS (alias de los tokens --navy-*, --sapphire-*, etc.):
//   home          → brand-800     (Navy / autoridad)
//   extract       → primary-500   (Sapphire / datos)
//   reconstruct   → info-500      (Violet / reconstrucción)
//   design        → success-500   (Emerald / construcción)
//   govern        → warning-500   (Amber / control)
//   intelligence  → primary-600   (Sapphire oscuro / analítica)
//   ai            → ai-500        (Cyan / EXCLUSIVO IA)
//   admin         → brand-700     (Navy oscuro)
// Uso de Tailwind nativo permite opacidad `/10` (CSS var hex no soporta alpha).
const workspaceColors: Record<WorkspaceColor, { border: string; bg: string; text: string; hover: string }> = {
  home: {
    border: 'border-l-brand-800',
    bg:     'bg-brand-800/10',
    text:   'text-brand-800 dark:text-brand-100',
    hover:  'hover:bg-brand-800/10',
  },
  extract: {
    border: 'border-l-primary-500',
    bg:     'bg-primary-500/10',
    text:   'text-primary-500 dark:text-primary-400',
    hover:  'hover:bg-primary-500/10',
  },
  reconstruct: {
    border: 'border-l-info-500',
    bg:     'bg-info-500/10',
    text:   'text-info-500',
    hover:  'hover:bg-info-500/10',
  },
  design: {
    border: 'border-l-success-500',
    bg:     'bg-success-500/10',
    text:   'text-success-500',
    hover:  'hover:bg-success-500/10',
  },
  govern: {
    border: 'border-l-warning-500',
    bg:     'bg-warning-500/10',
    text:   'text-warning-500',
    hover:  'hover:bg-warning-500/10',
  },
  intelligence: {
    border: 'border-l-primary-600',
    bg:     'bg-primary-600/10',
    text:   'text-primary-600 dark:text-primary-400',
    hover:  'hover:bg-primary-600/10',
  },
  ai: {
    // DS: Cyan EXCLUSIVO para módulos de IA
    border: 'border-l-ai-500',
    bg:     'bg-ai-500/15',
    text:   'text-ai-500',
    hover:  'hover:bg-ai-500/10',
  },
  admin: {
    border: 'border-l-brand-700',
    bg:     'bg-brand-700/10',
    text:   'text-brand-700 dark:text-brand-200',
    hover:  'hover:bg-brand-700/10',
  },
}

// A.L.D.O 3.0 Workspace Navigation — Nav Restructure Fase 1
// Pipeline: EXTRACT → RECONSTRUCT → DESIGN → GOVERN
// adminLevel on workspace: minimum role level to see entire workspace (lower = more privileged)
// feature: feature flag required (checked via usePermission)
// group/groupFirst on items: semantic group headers in sidebar
const workspaceNavigation: WorkspaceSection[] = [
  // AI — Primero en la lista de módulos. Chat (Asistente IA) y Canvas (Agentes) ocultos:
  // el único módulo visible es el cerebro de razonamiento (renombrado a marca comercial vía i18n).
  {
    id: 'ai',
    name: 'nav.ws.ai',
    icon: Sparkles,
    color: 'ai',
    adminLevel: 3,
    items: [
      { name: 'nav.ai.razonamiento', href: '/ai/razonamiento', icon: Brain, permission: 'ai.assistant.access', product: 'ai' },
    ],
  },
  // HOME — Dashboard and notifications
  {
    id: 'home',
    name: 'nav.ws.home',
    icon: LayoutDashboard,
    color: 'home',
    adminLevel: 3,
    items: [
      { name: 'nav.home.dashboard', href: '/home', icon: LayoutDashboard },
      { name: 'nav.home.notifications', href: '/home/notifications', icon: Bell },
      { name: 'nav.home.profile', href: '/home/profile', icon: User },
    ],
  },
  // EXTRACT — Connections and data extraction (pipeline step 1)
  {
    id: 'extract',
    name: 'nav.ws.extract',
    icon: PlugZap,
    color: 'extract',
    adminLevel: 3,
    product: 'data',
    items: [
      // GROUP: conectar
      { name: 'nav.extract.erp', href: '/extract/erp', icon: ServerCog, group: 'nav.group.conectar', groupFirst: true, permission: 'erp.connection.read' },
      { name: 'nav.extract.sql', href: '/extract/sql', icon: Database, group: 'nav.group.conectar', permission: 'sql.connection.read' },
      // GROUP: configurar
      { name: 'nav.extract.sources', href: '/extract/sources', icon: Server, group: 'nav.group.configurar', groupFirst: true, permission: 'datasource.manage' },
      { name: 'nav.extract.sap', href: '/extract/sap', icon: FileCode, group: 'nav.group.configurar', permission: 'erp.connection.read', product: 'data' },
      { name: 'nav.extract.odata', href: '/extract/odata', icon: Compass, group: 'nav.group.configurar', permission: 'erp.odata.read' },
      // GROUP: ejecutar
      { name: 'nav.extract.center', href: '/extract/center', icon: Play, group: 'nav.group.ejecutar', groupFirst: true, permission: 'pm.extraction.read', product: 'pm' },
      // GROUP: config (admin only)
      { name: 'nav.extract.endpoints', href: '/extract/endpoints', icon: Settings, group: 'nav.group.config', groupFirst: true, adminLevel: 3, permission: 'admin.odata.read' },
    ],
  },
  // RECONSTRUCT — Discovery and analysis (pipeline step 2)
  {
    id: 'reconstruct',
    name: 'nav.ws.reconstruct',
    icon: BarChart3,
    color: 'reconstruct',
    adminLevel: 3,
    feature: 'module_process_mining',
    product: 'pm',
    items: [
      // GROUP: descubrir
      { name: 'nav.reconstruct.discovery', href: '/reconstruct/discovery', icon: Search, group: 'nav.group.descubrir', groupFirst: true, permission: 'pm.discovery.read' },
      { name: 'nav.reconstruct.salDiscovery', href: '/reconstruct/sal-discovery', icon: Shield, group: 'nav.group.descubrir', permission: 'pm.discovery.read' },
      { name: 'nav.reconstruct.cases', href: '/reconstruct/cases', icon: Layers, group: 'nav.group.descubrir', permission: 'pm.variants.read' },
      // GROUP: medir
      { name: 'nav.reconstruct.kpis', href: '/reconstruct/kpis', icon: Gauge, group: 'nav.group.medir', groupFirst: true, permission: 'pm.kpis.read' },
      { name: 'nav.reconstruct.conformance', href: '/reconstruct/conformance', icon: Target, group: 'nav.group.medir', permission: 'pm.conformance.read' },
      { name: 'nav.reconstruct.heatmaps', href: '/reconstruct/heatmaps', icon: Flame, group: 'nav.group.medir', permission: 'pm.heatmaps.read' },
      { name: 'nav.reconstruct.users', href: '/reconstruct/users', icon: Activity, group: 'nav.group.medir', permission: 'pm.discovery.read' },
      { name: 'nav.reconstruct.compare', href: '/reconstruct/compare', icon: GitMerge, group: 'nav.group.medir', permission: 'pm.kpis.read' },
      // GROUP: sintetizar
      { name: 'nav.reconstruct.analysis', href: '/reconstruct/analysis', icon: Sparkles, group: 'nav.group.sintetizar', groupFirst: true, permission: 'pm.analysis.read' },
      { name: 'nav.reconstruct.relevamiento', href: '/reconstruct/relevamiento', icon: FileText, group: 'nav.group.sintetizar', product: 'pm', permission: 'pm.analysis.read' },
      // GROUP: config (GAM admin only)
      { name: 'nav.reconstruct.config', href: '/reconstruct/config', icon: Settings, group: 'nav.group.config', groupFirst: true, adminLevel: 1, permission: 'pm.config.read' },
    ],
  },
  // FAROS — Fit-to-Standard (top-level module, consulting). Gated by feature flag
  // module_faros (consultant-only: global OFF, enabled por holding de consultoria).
  // GAM Admin lo ve siempre (bypass). El backend revalida (FAROS_CONSULTANT_HOLDINGS).
  {
    id: 'faros',
    name: 'nav.ws.faros',
    icon: Compass,
    color: 'reconstruct',
    adminLevel: 3,
    feature: 'module_faros',
    items: [
      { name: 'nav.faros', href: '/analytics/faros', icon: Compass, product: 'pm' },
    ],
  },
  // DESIGN — Modeling and automation (pipeline step 3)
  {
    id: 'design',
    name: 'nav.ws.design',
    icon: PenTool,
    color: 'design',
    adminLevel: 3,
    items: [
      // GROUP: modelar
      { name: 'nav.design.processes', href: '/design/processes', icon: Workflow, group: 'nav.group.modelar', groupFirst: true, permission: 'bp.template.read', product: 'pm' },
      { name: 'nav.design.bpmn', href: '/design/bpmn', icon: PenTool, group: 'nav.group.modelar', permission: 'pm.designer.read', product: 'pm' },
      { name: 'nav.design.diagrams', href: '/design/diagrams', icon: FolderOpen, group: 'nav.group.modelar', permission: 'pm.diagrams.read', product: 'pm' },
      // GROUP: generar
      { name: 'nav.design.fiori', href: '/design/fiori', icon: Rocket, group: 'nav.group.generar', groupFirst: true, permission: 'fiori.mockup.read', product: 'fiori' },
      { name: 'nav.design.convert', href: '/admin/convert', icon: Wand2, group: 'nav.group.generar', adminLevel: 3, product: 'fiori' },
      // GROUP: documentar
      { name: 'nav.design.docs', href: '/design/docs', icon: BookOpen, group: 'nav.group.documentar', groupFirst: true, permission: 'docs.abap.read', product: 'docs' },
      { name: 'nav.design.library', href: '/design/library', icon: Library, group: 'nav.group.documentar', permission: 'docs.library.read', product: 'docs' },
      // GROUP: auditar
      { name: 'nav.design.auditAnalytics', href: '/audit-analytics/jobs', icon: Fingerprint, group: 'nav.group.auditar', groupFirst: true, permission: 'docs.library.read', product: 'docs' },
      // GROUP: desplegar
      { name: 'nav.design.transport', href: '/design/transport', icon: Truck, group: 'nav.group.desplegar', groupFirst: true, permission: 'transport.order.read', product: 'docs' },
    ],
  },
  // GOVERN — Risk and compliance (pipeline step 4)
  {
    id: 'govern',
    name: 'nav.ws.govern',
    icon: Shield,
    color: 'govern',
    adminLevel: 3,
    items: [
      // GROUP: visión general
      { name: 'nav.govern.dashboard', href: '/govern/dashboard', icon: LayoutDashboard, group: 'nav.group.visionGeneral', groupFirst: true },
      // GROUP: evaluar riesgo
      { name: 'nav.govern.risk', href: '/govern/risk', icon: AlertTriangle, group: 'nav.group.evaluarRiesgo', groupFirst: true },
      { name: 'nav.govern.blast', href: '/govern/blast', icon: Flame, group: 'nav.group.evaluarRiesgo' },
      { name: 'nav.govern.rca', href: '/govern/rca', icon: Brain, group: 'nav.group.evaluarRiesgo', permission: 'pm.rca.read' },
      // GROUP: compliance
      { name: 'nav.govern.sod', href: '/govern/sod', icon: UserX, group: 'nav.group.compliance', groupFirst: true },
      { name: 'nav.govern.sal', href: '/govern/sal', icon: Shield, group: 'nav.group.compliance' },
      { name: 'nav.govern.changes', href: '/govern/changes', icon: GitBranch, group: 'nav.group.compliance' },
      // GROUP: operar
      { name: 'nav.govern.monitoring', href: '/govern/monitoring', icon: ShieldAlert, group: 'nav.group.operar', groupFirst: true, permission: 'pm.monitoring.read' },
      { name: 'nav.govern.alerts', href: '/govern/alerts', icon: Bell, group: 'nav.group.operar' },
      { name: 'nav.govern.reports', href: '/govern/reports', icon: FileText, group: 'nav.group.operar' },
      { name: 'nav.govern.licenseOptimizer', href: '/govern/license-optimizer', icon: BadgeDollarSign, group: 'nav.group.operar', permission: 'license.optimizer.read' },
    ],
  },
  // MONARCA — Migración de datos maestros (BADM)
  // Comercialmente "Monarca"; técnicamente BADM. Producto independiente del
  // pipeline EXTRACT/RECONSTRUCT/DESIGN/GOVERN: gobierna el ciclo de vida
  // de master data (BP, Material, Financiero, Logístico) hacia S/4HANA.
  {
    id: 'monarca',
    name: 'nav.ws.monarca',
    icon: Repeat,
    color: 'extract',
    adminLevel: 3,
    // P-MENU sec 13 audit Baco 2026-05-12 — consolidación 12 → 7 ítems.
    // El menú original reflejaba la arquitectura backend (un router = un
    // ítem); el nuevo refleja el flujo de trabajo del usuario funcional.
    // Las rutas viejas siguen redirigidas (App.tsx) para no romper
    // bookmarks ni docs externos.
    items: [
      { name: 'nav.monarca.dashboard', href: '/monarca', icon: LayoutDashboard },
      // "Datos" abarca todos los BOs vía workspace + tabs internos.
      // Lleva al dashboard del proyecto activo; desde ahí cada BO tiene
      // su workspace con tab Datos / Calidad / Dedup / Mapeo / IA.
      { name: 'nav.monarca.data', href: '/monarca', icon: PackageOpen },
      // Quality unifica Reglas + Resultados + Tareas (Workflow) + Historial (Audit).
      { name: 'nav.monarca.quality', href: '/monarca/quality', icon: Shield },
      // Dedup unifica Reglas + Detectar + Clusters.
      { name: 'nav.monarca.dedup', href: '/monarca/dedup', icon: Copy },
      // Mapping: templates CVI + grid editable.
      { name: 'nav.monarca.mappings', href: '/monarca/mappings', icon: Layers },
      // Cockpit final.
      { name: 'nav.monarca.cockpit', href: '/monarca/cockpit', icon: FileCode },
      // Admin / Setup agrupa lo de uso único o configuración avanzada
      // (Catálogo, Pipeline runs, Enriquecimiento). Visible para
      // adminLevel >= 3 (Holding Admin) — los menos usados ya estaban
      // gateados a ese nivel.
      { name: 'nav.monarca.admin', href: '/monarca/admin', icon: ScrollText },
    ],
  },
  // ADMIN — Configuration and organization management
  {
    id: 'admin',
    name: 'nav.ws.admin',
    icon: Settings,
    color: 'admin',
    adminOnly: true,
    items: [
      // Organization items - filtered by adminLevel and permissions
      // INC-061: Permission codes must match seed data in rbac_seed.py
      { name: 'nav.organization.channelPartners', href: '/admin/channel-partners', icon: Briefcase, adminLevel: 1, permission: 'admin.cp.read' },
      { name: 'nav.organization.holdings', href: '/admin/holdings', icon: Building2, adminLevel: 2, permission: 'admin.holding.read' },
      { name: 'nav.organization.companies', href: '/admin/empresas', icon: Building, adminLevel: 3, permission: 'admin.empresa.read' },
      { name: 'nav.organization.areas', href: '/admin/areas', icon: Layers, adminLevel: 3, permission: 'admin.area.read' },
      // User management
      { name: 'nav.settings.users', href: '/admin/users', icon: Users, adminLevel: 5, permission: 'admin.user.read' },
      // System settings - GAM Admin only
      { name: 'nav.settings.rbac', href: '/admin/rbac', icon: Shield, adminLevel: 1, permission: 'admin.rbac.read' },
      { name: 'nav.settings.prompts', href: '/admin/prompts', icon: FileText, adminLevel: 1, permission: 'admin.prompts.read' },
      { name: 'nav.settings.teamsBot', href: '/admin/teams-bot', icon: Bot, adminLevel: 3, permission: 'admin.holding.read' },
      { name: 'nav.settings.aiMetrics', href: '/admin/ai-metrics', icon: BarChart3, adminLevel: 3 },
      { name: 'nav.settings.monarcaLogs', href: '/admin/monarca-logs', icon: Activity, adminLevel: 3 },
      { name: 'nav.settings.readiness', href: '/admin/readiness', icon: FileText, adminLevel: 3 },
      { name: 'nav.settings.verticals', href: '/admin/verticals', icon: Sparkles, adminLevel: 1 },
    ],
  },
]

export default function MainLayout() {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const { user, logout, holdings, activeHoldingId, switchHolding } = useAuthStore()
  const { hasPermission, hasAnyPermission, isFeatureEnabled, isGAMAdmin, hasProduct } = usePermission()
  const { isDark, toggle: toggleTheme } = useTheme()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [langMenuOpen, setLangMenuOpen] = useState(false)
  const [holdingMenuOpen, setHoldingMenuOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [switchingHolding, setSwitchingHolding] = useState(false)

  // Track which domain sections are expanded
  const [expandedDomains, setExpandedDomains] = useState<Record<string, boolean>>(() => {
    // Initially expand domains that contain the current path
    const initial: Record<string, boolean> = {}
    workspaceNavigation.forEach(domain => {
      const hasActiveItem = domain.items.some(item => location.pathname.startsWith(item.href))
      initial[domain.id] = hasActiveItem
    })
    return initial
  })

  // INC-031: Get active holding name
  const activeHolding = holdings.find(h => h.id === activeHoldingId)

  // INC-031: Handle holding switch
  const handleSwitchHolding = async (holdingId: string) => {
    if (holdingId === activeHoldingId) {
      setHoldingMenuOpen(false)
      return
    }
    setSwitchingHolding(true)
    try {
      await switchHolding(holdingId)
      setHoldingMenuOpen(false)
      // Reload the page to refresh data with new holding context
      window.location.reload()
    } catch {
      // Error handled by store
    } finally {
      setSwitchingHolding(false)
    }
  }

  // Check if user has admin permissions (role_level 1-5 are admin levels)
  const isAdmin = user?.role_level && user.role_level <= 5

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0]

  // Toggle domain expansion
  const toggleDomain = (domainId: string) => {
    setExpandedDomains(prev => ({
      ...prev,
      [domainId]: !prev[domainId],
    }))
  }

  // Check if any item in domain is active
  const isDomainActive = (domain: WorkspaceSection) => {
    return domain.items.some(item => location.pathname.startsWith(item.href))
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      {/* DS: Sidebar fondo siempre Navy 950 */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-brand-950 border-r border-brand-800 transform transition-transform duration-200 ease-in-out lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* DS: Logo area Navy 900 */}
          <div className="h-16 flex items-center px-6 border-b border-brand-800 bg-brand-900">
            <Link to="/dashboard" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-brand-800 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">A</span>
              </div>
              <span className="font-bold text-xl text-white">A.L.D.O</span>
              <span className="text-xs bg-white/10 text-white/70 px-2 py-0.5 rounded">3.0</span>
            </Link>
          </div>

          {/* Navigation by Domains */}
          <nav className="flex-1 py-4 overflow-y-auto">
            {workspaceNavigation.map((domain) => {
              // Skip admin-only sections for non-admin users
              if (domain.adminOnly && !isAdmin) return null

              // Skip domains based on adminLevel:
              // adminLevel=3 means Admin Holding+ (roles 3,4,5,6,7) can see it
              // GAM Admin (1) always sees everything
              // Admin CP (2) should NOT see operational domains (adminLevel=3)
              if (domain.adminLevel && user?.role_level) {
                if (user.role_level !== 1 && user.role_level < domain.adminLevel) return null
              }

              // RBAC: Skip domains if feature flag is not enabled (GAM Admin bypasses)
              if (domain.feature && !isGAMAdmin && !isFeatureEnabled(domain.feature)) {
                return null
              }

              // INC-077: Skip domains if product is not enabled for holding (GAM Admin bypasses via hasProduct)
              if (domain.product && !hasProduct(domain.product)) {
                return null
              }

              // RBAC: Filter items by permissions and adminLevel
              const visibleItems = domain.items.filter((item) => {
                // Check admin level first
                if (item.adminLevel && user?.role_level) {
                  if (user.role_level > item.adminLevel) return false
                }

                // RBAC: Check permission (GAM Admin bypasses via hasPermission)
                if (item.permission && !hasPermission(item.permission)) {
                  return false
                }

                // RBAC: Check multiple permissions (OR logic)
                if (item.permissions && item.permissions.length > 0) {
                  if (!hasAnyPermission(item.permissions)) return false
                }

                // INC-077: Check product at item level (GAM Admin bypasses via hasProduct)
                if (item.product && !hasProduct(item.product)) {
                  return false
                }

                return true
              })

              // Skip domain if no visible items
              if (visibleItems.length === 0) return null

              const isExpanded = expandedDomains[domain.id]
              const isActive = isDomainActive(domain)
              const colors = workspaceColors[domain.color]

              return (
                <div key={domain.id} className="mb-1">
                  {/* Domain Header */}
                  <button
                    onClick={() => toggleDomain(domain.id)}
                    className={cn(
                      'w-full flex items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors',
                      'border-l-4',
                      isActive ? colors.border : 'border-l-transparent',
                      isActive ? colors.bg : 'hover:bg-white/[0.06]',
                      isActive ? colors.text : 'text-white/50 hover:text-white/75'
                    )}
                  >
                    <div className="flex items-center space-x-2">
                      {/* DS v1.0: sidebar nav icons = 14px */}
                      <domain.icon className={cn('w-3.5 h-3.5', isActive && colors.text)} strokeWidth={1.5} />
                      <span>{t(domain.name)}</span>
                    </div>
                    <ChevronRight
                      className={cn(
                        'w-4 h-4 transition-transform',
                        isExpanded && 'rotate-90'
                      )}
                    />
                  </button>

                  {/* Domain Items */}
                  {isExpanded && (
                    <div className="mt-1 space-y-0.5">
                      {visibleItems.map((item, idx) => {
                          const isItemActive = location.pathname === item.href ||
                            (item.href !== '/home' && location.pathname.startsWith(item.href))

                          // Generic group headers: render divider + label when groupFirst === true
                          const showGroupDivider = item.groupFirst === true && idx > 0
                          const showGroupLabel = item.groupFirst === true && item.group

                          return (
                            <div key={item.href}>
                              {showGroupDivider && (
                                <div className="mx-3 ml-4 my-1 border-t border-white/10" />
                              )}
                              {showGroupLabel && (
                                <span className="px-4 ml-4 pt-2 pb-1 text-[9px] font-bold uppercase tracking-[1px] text-white/25 block select-none">
                                  {t(item.group!)}
                                </span>
                              )}
                              <Link
                                to={item.href}
                                className={cn(
                                  'flex items-center space-x-3 px-4 py-2 ml-4 text-sm font-medium transition-colors rounded-r-lg',
                                  'border-l-2',
                                  isItemActive
                                    ? cn(colors.border, colors.bg, colors.text)
                                    : cn('border-l-transparent text-white/50 hover:text-white/75 hover:bg-white/[0.06]')
                                )}
                                onClick={() => setSidebarOpen(false)}
                              >
                                {/* DS v1.0: sidebar nav icons = 14px, stroke 1.5 */}
                                <item.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                                <span className="flex-1">{t(item.name)}</span>
                                {item.badge != null && item.badge > 0 && (
                                  <span className="ml-auto bg-danger-500 text-white text-[9px] font-bold px-[5px] py-[1px] rounded-[10px] leading-none">
                                    {item.badge}
                                  </span>
                                )}
                              </Link>
                            </div>
                          )
                        })}
                    </div>
                  )}

                  {/* Separator between workspace groups */}
                  {['home', 'govern', 'ai'].includes(domain.id) && (
                    <div className="mx-4 my-2 border-t border-white/10" />
                  )}
                </div>
              )
            })}
          </nav>

          {/* DS: User section - Bottom left */}
          <div className="border-t border-brand-800">
            {/* User profile button with popup menu */}
            <div className="relative p-2">
              <button
                className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-white/[0.06] transition-colors"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-primary-foreground">
                    {user?.full_name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium truncate text-white/90">{user?.full_name}</p>
                  <p className="text-xs text-white/50 truncate">{user?.email}</p>
                </div>
                <ChevronDown className={cn('w-4 h-4 text-white/40 transition-transform flex-shrink-0', userMenuOpen && 'rotate-180')} />
              </button>

              {/* User menu popup */}
              {userMenuOpen && (
                <div className="absolute bottom-full left-2 right-2 mb-2 bg-popover border rounded-lg shadow-lg py-1 z-50">
                  {/* Role badge */}
                  {user?.role && (
                    <div className="px-3 py-2 border-b">
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <KeyRound className="w-3 h-3" />
                        {t(`roles.${user.role}`, user.role)}
                      </p>
                    </div>
                  )}

                  {/* Menu items */}
                  <Link
                    to="/profile"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center space-x-2"
                    onClick={() => {
                      setUserMenuOpen(false)
                      setSidebarOpen(false)
                    }}
                  >
                    <User className="w-4 h-4" />
                    <span>{t('nav.profile')}</span>
                  </Link>

                  <Link
                    to="/settings"
                    className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center space-x-2"
                    onClick={() => {
                      setUserMenuOpen(false)
                      setSidebarOpen(false)
                    }}
                  >
                    <Settings className="w-4 h-4" />
                    <span>{t('nav.settings.title')}</span>
                  </Link>

                  <div className="border-t my-1" />

                  <button
                    className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center space-x-2 text-destructive"
                    onClick={() => {
                      setUserMenuOpen(false)
                      logout()
                    }}
                  >
                    <LogOut className="w-4 h-4" />
                    <span>{t('auth.logout')}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="h-16 border-b bg-card sticky top-0 z-30">
          <div className="h-full px-4 flex items-center justify-between">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-accent"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>

            <div className="flex-1" />

            {/* INC-031: Holding switcher - only show if user has multiple holdings */}
            {holdings.length > 1 && (
              <div className="relative mr-2">
                <button
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-accent"
                  onClick={() => setHoldingMenuOpen(!holdingMenuOpen)}
                  disabled={switchingHolding}
                >
                  <Building2 className="w-4 h-4" />
                  <span className="text-sm max-w-32 truncate">{activeHolding?.name || t('auth.selectHolding')}</span>
                  <ChevronDown className={cn('w-4 h-4 transition-transform', holdingMenuOpen && 'rotate-180')} />
                </button>

                {holdingMenuOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-popover border rounded-lg shadow-lg py-1 z-50">
                    <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                      {t('auth.switchHolding')}
                    </div>
                    {holdings.map((holding) => (
                      <button
                        key={holding.id}
                        className={cn(
                          'w-full px-4 py-2 text-left text-sm hover:bg-accent flex items-center justify-between',
                          holding.id === activeHoldingId && 'bg-accent'
                        )}
                        onClick={() => handleSwitchHolding(holding.id)}
                        disabled={switchingHolding}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{holding.name}</div>
                          <div className="text-xs text-muted-foreground">{holding.code}</div>
                        </div>
                        {holding.id === activeHoldingId && (
                          <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded ml-2">
                            {t('common.active')}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* DS: Dark mode toggle */}
            <button
              className="p-2 rounded-lg hover:bg-accent transition-colors mr-1"
              onClick={toggleTheme}
              title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            >
              {isDark
                ? <Sun className="w-4 h-4 text-muted-foreground" />
                : <Moon className="w-4 h-4 text-muted-foreground" />
              }
            </button>

            {/* Language selector */}
            <div className="relative">
              <button
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-accent"
                onClick={() => setLangMenuOpen(!langMenuOpen)}
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm">{currentLang.flag} {currentLang.name}</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {langMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-popover border rounded-lg shadow-lg py-1 z-50">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={cn(
                        'w-full px-4 py-2 text-left text-sm hover:bg-accent flex items-center space-x-2',
                        lang.code === i18n.language && 'bg-accent'
                      )}
                      onClick={() => {
                        i18n.changeLanguage(lang.code)
                        setLangMenuOpen(false)
                      }}
                    >
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* User avatar - hidden on desktop (profile menu is in sidebar) */}
            <div className="lg:hidden ml-2">
              <Link
                to="/profile"
                className="flex items-center px-2 py-1 rounded-lg hover:bg-accent"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-foreground">
                    {user?.full_name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              </Link>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
